const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
  message?: string;
}

interface ProfileResponse {
  success: boolean;
  user: User;
  message?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.message || 'Request failed');
    }
    return response.json();
  }

  // Authentication endpoints
  async signup(data: { name: string; email: string; password: string }): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse<AuthResponse>(response);
  }

  async login(data: { email: string; password: string }): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse<AuthResponse>(response);
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return this.handleResponse<ApiResponse>(response);
  }

  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<User>(response);
  }

  async logout(): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ApiResponse>(response);
  }

  // Profile endpoints
  async getProfile(): Promise<ProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/api/profile`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ProfileResponse>(response);
  }

  async updateProfile(data: { name: string; email: string }): Promise<ProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/api/profile`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ProfileResponse>(response);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/profile/password`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse>(response);
  }

  // Dashboard endpoints
  async getDashboardSummary() {
    const response = await fetch(`${API_BASE_URL}/api/dashboard/summary`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getRecentTrades() {
    const response = await fetch(`${API_BASE_URL}/api/trade/history`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getPortfolioBreakdown() {
    const response = await fetch(`${API_BASE_URL}/api/dashboard/portfolio-breakdown`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Portfolio endpoints
  async getPortfolio() {
    const response = await fetch(`${API_BASE_URL}/api/trade/portfolio`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Trading endpoints
  async placeTrade(tradeData: any) {
    const response = await fetch(`${API_BASE_URL}/api/trade`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(tradeData),
    });
    return this.handleResponse(response);
  }

  async getOrderTypes() {
    const response = await fetch(`${API_BASE_URL}/api/trade/order-types`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }
}

export const apiService = new ApiService();
export { ApiError };
